import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppContext, useAppDispatch } from '../../AppContext'
import { Actions as Action, Actions } from '../../AppReducer'
import ChatContainer from './ChatContainer'
import { useApolloClient, useLazyQuery, useMutation, useSubscription } from '@apollo/client/react'
import AddChat from '../../graphql/queries/AddChat'
import RemoveChat from '../../graphql/queries/RemoveChat'
import EditChat from '../../graphql/queries/EditChat'
import AddChatReaction from '../../graphql/queries/AddChatReaction'
import RemoveChatReaction from '../../graphql/queries/RemoveChatReaction'
import History from '../../graphql/queries/History'
import RoomEvents from '../../graphql/queries/RoomEvents'
import useCurrentRoom from '../../hooks/useCurrentRoom'
import Spinner from '../../ui/Spinner'
import useCurrentUser from '../../hooks/useCurrentUser'
import { Textarea } from '@mantine/core'
import EmojiPickerButton from '../../ui/EmojiPickerButton'
import TrackLinkPopover from '../../ui/TrackLinkPopover'
import InfoBubble from './InfoBubble'
import ChatBubble from './ChatBubble'
import { useModalStack } from '../../ui/ModalStack'
import { connectionState } from '../../graphql/links/websocket'
import logout from '../../auth/logout'
import { validTokenExists } from '../../auth/token'

export default function Chat() {

    const dispatch = useAppDispatch()
    const apolloClient = useApolloClient()
    const { pushModal } = useModalStack()

    const [commandHistory, setCommandHistory] = useState([])
    const [line, setLine] = useState('')
    const [lineBackup, setLineBackup] = useState(null)
    const [commandHistoryCursor, setCommandHistoryCursor] = useState(0)
    const inputRef = useRef(null)
    const [scrollingPaused, setScrollingPaused] = useState(false)
    const terminalContainerRef = useRef(null)
    const { activeTab } = useAppContext()
    const [lastScrollHeight, setLastScrollHeight] = useState(0)
    const [optimisticChats, setOptimisticChats] = useState([])
    const [optimisticChatReactions, setOptimisticChatReactions] = useState([])
    const [animatedReactions, setAnimatedReactions] = useState(new Set())
    const [selectedMessageId, setSelectedMessageId] = useState(null)
    const [menuPosition, setMenuPosition] = useState(null)
    const [removingMessages, setRemovingMessages] = useState(new Set())
    const [editingMessageId, setEditingMessageId] = useState(null)
    const [editingText, setEditingText] = useState('')
    const [originalText, setOriginalText] = useState('')
    const [hoveredOtherMessageId, setHoveredOtherMessageId] = useState(null)
    const [otherMessageMenuPosition, setOtherMessageMenuPosition] = useState(null)
    const menuRef = useRef(null)
    const otherMessageMenuRef = useRef(null)
    const [isRecording, setIsRecording] = useState(false)
    const recognitionRef = useRef(null)
    const [speechRecognitionSupported, setSpeechRecognitionSupported] = useState(false)
    const voiceInputHandlerRef = useRef(null)
    const pendingVoiceSubmitRef = useRef(null)
    const [voiceSubmitTrigger, setVoiceSubmitTrigger] = useState(0)
    const [isDisconnected, setIsDisconnected] = useState(false)
    const [isReconnecting, setIsReconnecting] = useState(false)
    const shouldRefetchHistoryRef = useRef(false)

    // Refocus on the input box when returning to chat
    useEffect(() => {
        if (activeTab === 'listen') {
            inputRef.current?.focus()
        }
    }, [activeTab, inputRef.current])

    const [online, setOnline] = useState(navigator.onLine)
    useEffect(() => {
        const handleOnline = () => setOnline(true)
        const handleOffline = () => setOnline(false)

        window.addEventListener('online', handleOnline)
        window.addEventListener('offline', handleOffline)

        return () => {
            window.removeEventListener('online', handleOnline)
            window.removeEventListener('offline', handleOffline)
        }
    }, [])

    // Subscribe to WebSocket connection state
    useEffect(() => {
        const unsubscribe = connectionState.subscribe((isConnected, hasConnectedOnce) => {
            // console.log(`WebSocket connection state changed: ${isConnected ? 'connected' : 'disconnected'}, hasConnectedOnce: ${hasConnectedOnce}`)
            if (!isConnected && hasConnectedOnce) {
                // If there is no valid token (and no refresh token to renew it), log out
                // instead of showing the reconnect overlay — the session has expired.
                if (!validTokenExists()) {
                    // console.log('WebSocket disconnected and no valid auth token exists — logging out')
                    logout()
                    return
                }
                // Only show overlay if we've connected before (not on initial load)
                setIsDisconnected(true)
                shouldRefetchHistoryRef.current = true
            } else if (isConnected) {
                // Connection restored - clear disconnected state
                // History refetch will happen in onData when first message arrives
                setIsDisconnected(false)
                setIsReconnecting(false)
            }
        })

        return unsubscribe
    }, [])

    // Subscribe to WebSocket auth-failure signals (e.g. server rejects with 4401)
    useEffect(() => {
        const unsubscribe = connectionState.onAuthFailed(() => {
            // console.log('WebSocket auth failure received — logging out')
            logout()
        })
        return unsubscribe
    }, [])

    // Initialize speech recognition
    useEffect(() => {
        // Check if browser supports speech recognition and is not Firefox
        const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1
        if (isFirefox) {
            setSpeechRecognitionSupported(false)
            return
        }

        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
        if (!SpeechRecognition) {
            console.warn('Speech recognition not supported in this browser')
            setSpeechRecognitionSupported(false)
            return
        }

        setSpeechRecognitionSupported(true)
        const recognition = new SpeechRecognition()
        recognition.continuous = false
        recognition.interimResults = false
        recognition.lang = 'en-US'

        recognition.onresult = (event) => {
            const transcript = event.results[0][0].transcript
            setIsRecording(false)

            // Call the handler from ref which has access to latest dependencies
            if (voiceInputHandlerRef.current) {
                voiceInputHandlerRef.current(transcript)
            }
        }

        recognition.onerror = (event) => {
            console.error('Speech recognition error:', event.error)
            setIsRecording(false)
            if (event.error === 'not-allowed') {
                alert('Microphone access denied. Please enable microphone permissions in your browser settings.')
            }
        }

        recognition.onend = () => {
            setIsRecording(false)
        }

        recognitionRef.current = recognition

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.abort()
            }
        }
    }, [])

    const room = useCurrentRoom()
    const roomId = room?.id
    const user = useCurrentUser()
    const [ addChatMutation ] = useMutation(AddChat)
    const [ removeChatMutation ] = useMutation(RemoveChat)
    const [ editChatMutation ] = useMutation(EditChat)
    const [ addChatReactionMutation ] = useMutation(AddChatReaction)
    const [ removeChatReactionMutation ] = useMutation(RemoveChatReaction)

    // Define processInput with useCallback so it can be safely used in other callbacks
    const processInput = useCallback(async (text) => {
        // Add optimistic chat message
        const optimisticChat = {
            id: `optimistic-${Date.now()}-${Math.random()}`,
            text,
            timestamp: new Date().toISOString()
        }
        setOptimisticChats(prev => [...prev, optimisticChat])

        try {
            await addChatMutation({ variables: { roomId, text } })
        } catch (error) {
            console.error('failed to add chat', error)

            // Check if this is a network error
            const isNetworkError = error.networkError ||
                                  error.message?.includes('Failed to fetch') ||
                                  error.message?.includes('NetworkError') ||
                                  error.message?.includes('Network request failed') ||
                                  error.message?.includes('ERR_INTERNET_DISCONNECTED')

            if (isNetworkError) {
                // console.log('Network error detected, showing disconnection overlay')
                setIsDisconnected(true)
                shouldRefetchHistoryRef.current = true
            }

            // Remove the optimistic chat on error
            setOptimisticChats(prev => prev.filter(chat => chat.id !== optimisticChat.id))
        }
    }, [roomId, addChatMutation])

    // Keep the voice input handler ref updated with latest dependencies
    const handleVoiceInputSubmit = useCallback((transcript) => {
        const newLine = line ? line + ' ' + transcript : transcript

        // Store the text to submit in a ref
        if (newLine && newLine.length > 0) {
            pendingVoiceSubmitRef.current = newLine
            // Trigger the submission effect
            setVoiceSubmitTrigger(prev => prev + 1)
        }
    }, [line])

    // Handle voice input submission in a separate effect to avoid double execution
    useEffect(() => {
        if (voiceSubmitTrigger > 0 && pendingVoiceSubmitRef.current) {
            const textToSubmit = pendingVoiceSubmitRef.current
            pendingVoiceSubmitRef.current = null

            // Add to command history
            setCommandHistory(history => [...history, textToSubmit])
            setCommandHistoryCursor(0)
            // Process and send the input
            processInput(textToSubmit)
            // Clear the input
            setLine('')
            // Refocus textarea
            setTimeout(() => inputRef.current?.focus(), 0)
        }
    }, [voiceSubmitTrigger, processInput])

    // Update ref when callback changes
    useEffect(() => {
        voiceInputHandlerRef.current = handleVoiceInputSubmit
    }, [handleVoiceInputSubmit])

    const {
        loading: loadingSubscription,
        restart: restartSubscription
    } = useSubscription(RoomEvents, {
        onComplete,
        onData,
        onError,
        shouldResubscribe: true,
        skip: !roomId,
        variables: { id: roomId }
    })

    const [ fetchHistory, { data: historyData, fetchMore: fetchMoreHistory, loading: loadingHistory} ] = useLazyQuery(
        History, {
            notifyOnNetworkStatusChange: true,
            skip: !roomId,
            variables: { roomId }
        }
    )

    useEffect(() => {
        // console.log(`loadingSubscription: ${loadingSubscription}`)
        if (!loadingSubscription && roomId) {
            apolloClient.cache.evict({ id: 'ROOT_QUERY', fieldName: 'history' })
            fetchHistory({ variables: { roomId } })
        }
    }, [loadingSubscription, roomId])

    // Reconnect when coming back online (e.g., after computer sleep or network change)
    useEffect(() => {
        if (online && roomId && !loadingSubscription) {
            // console.log('Network restored, restarting subscription and refetching history')

            // Clear cache to avoid stale data
            apolloClient.cache.evict({ id: 'ROOT_QUERY', fieldName: 'history' })

            // Restart subscription to receive new events
            restartSubscription()

            // Refetch history to fill any gaps from the disconnection
            fetchHistory({ variables: { roomId } })
        }
    }, [online, roomId, loadingSubscription, restartSubscription, apolloClient, fetchHistory])


    useEffect(() => {
        const term = terminalContainerRef.current
        // console.log(`historyData changed; loadingHistory: ${loadingHistory}, historyCursor: ${historyData?.history?.cursor}`)
        if (term && Math.abs(term.scrollHeight - term.clientHeight - term.scrollTop) >= 1 && !scrollingPaused) {
            setTimeout(() => {
                const targetTop = term.scrollHeight - term.clientHeight
                term.scrollTo({ left: 0, top: targetTop, behavior: 'smooth' })
                // console.log(`got new history: scrolled to ${targetTop}`)
            }, 500)
        }
        if (lastScrollHeight) {
            setLastScrollHeight(0)
            const diff = term.scrollHeight - lastScrollHeight
            term.scrollTop += diff
            // console.log(`got new scrollback history: set scrollTop to ${term.scrollTop}`)
        }
    }, [historyData, loadingHistory])

    // Scroll to bottom when optimistic chats are added (if not paused)
    useEffect(() => {
        const term = terminalContainerRef.current
        if (term && optimisticChats.length > 0 && !scrollingPaused) {
            setTimeout(() => {
                const targetTop = term.scrollHeight - term.clientHeight
                term.scrollTo({ left: 0, top: targetTop, behavior: 'smooth' })
                // console.log(`optimistic chat added: scrolled to ${targetTop}`)
            }, 0)
        }
    }, [optimisticChats, scrollingPaused])

    // Close menu when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            // Don't close if clicking on any Mantine popover or emoji picker
            const isInPopover = event.target.closest('[data-radix-popper-content-wrapper]') ||
                                event.target.closest('.mantine-Popover-dropdown') ||
                                event.target.closest('em-emoji-picker')

            if (isInPopover) {
                return
            }

            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setSelectedMessageId(null)
                setMenuPosition(null)
            }
        }

        if (selectedMessageId) {
            document.addEventListener('mousedown', handleClickOutside)
            return () => document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [selectedMessageId])

    // Close other message menu when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            // Don't close if clicking on any Mantine popover or emoji picker
            const isInPopover = event.target.closest('[data-radix-popper-content-wrapper]') ||
                                event.target.closest('.mantine-Popover-dropdown') ||
                                event.target.closest('em-emoji-picker')

            if (isInPopover) {
                return
            }

            if (otherMessageMenuRef.current && !otherMessageMenuRef.current.contains(event.target)) {
                setHoveredOtherMessageId(null)
                setOtherMessageMenuPosition(null)
            }
        }

        if (hoveredOtherMessageId) {
            document.addEventListener('mousedown', handleClickOutside)
            return () => document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [hoveredOtherMessageId])

    function addCommandHistory() {
        const newHistory = [ line, ...commandHistory ]
        if (newHistory.length > 100)
            newHistory.pop()
        setCommandHistory(newHistory)
    }

    function handleKeyPress(e) {
        if (e.key === 'Enter') {
            addCommandHistory()
            processInput(line)
            setLine('')
        } else if (e.key === 'ArrowUp') {
            if (commandHistory.length === 0) {
                // Do nothing
            } else if (commandHistoryCursor === 0 && lineBackup == null) {
                setLineBackup(line)
                setLine(commandHistory[0])
                const end = commandHistory[0].length
                setTimeout(() => inputRef.current.setSelectionRange(end, end), 0)
            } else {
                const newCursor = Math.min(commandHistory.length - 1, commandHistoryCursor + 1)
                setCommandHistoryCursor(newCursor)
                setLine(commandHistory[newCursor])
                const end = commandHistory[newCursor].length
                setTimeout(() => inputRef.current.setSelectionRange(end, end), 0)
            }
        } else if (e.key === 'ArrowDown') {
            if (commandHistoryCursor === 0 && lineBackup != null) {
                setLine(lineBackup)
                setLineBackup(null)
            } else if (commandHistoryCursor > 0) {
                const newCursor = commandHistoryCursor - 1
                setCommandHistoryCursor(newCursor)
                setLine(commandHistory[newCursor])
                const end = commandHistory[newCursor].length
                inputRef.current.setSelectionRange(end, end)
            }
        }
    }

    function handleSendClick() {
        if (!line || line.length === 0) return
        addCommandHistory()
        processInput(line)
        setLine('')
        // Refocus textarea after send
        setTimeout(() => inputRef.current?.focus(), 0)
    }

    function handleVoiceInput() {
        if (!recognitionRef.current) {
            alert('Speech recognition is not supported in your browser')
            return
        }

        if (isRecording) {
            recognitionRef.current.stop()
            setIsRecording(false)
        } else {
            try {
                recognitionRef.current.start()
                setIsRecording(true)
            } catch (error) {
                console.error('Failed to start speech recognition:', error)
                alert('Failed to start voice recording. Please try again.')
            }
        }
    }

    function handleScroll(e) {
        const { scrollTop, scrollHeight, clientHeight } = e.target
        const atTop = scrollTop <= 0
        const atBottom = (scrollHeight - clientHeight - scrollTop) <= 1

        if (!atBottom && !scrollingPaused) setScrollingPaused(true)
        else if (atBottom && scrollingPaused) setScrollingPaused(false)

        const nextCursor = historyData?.history?.cursor
        if (!loadingHistory && atTop && nextCursor != null) {
            // console.log(`fetching more history atTop; cursor: ${nextCursor}`)
            fetchMoreHistory({ variables: { cursor: nextCursor }})
            if (terminalContainerRef.current) setLastScrollHeight(terminalContainerRef.current.scrollHeight)
        }
    }

    function onComplete(data) {
        // console.log(`SUBSCRIPTION COMPLETED: ${JSON.stringify(data)}`)
        if (!validTokenExists()) {
            // console.log('Subscription completed and no valid auth token exists — logging out')
            logout()
            return
        }
        setIsDisconnected(true)
        shouldRefetchHistoryRef.current = true
        setTimeout(restartSubscription, 5000)
    }

    function handleManualReconnect() {
        // console.log('Manual reconnection requested')
        if (!validTokenExists()) {
            // console.log('Manual reconnect attempted but no valid auth token exists — logging out')
            logout()
            return
        }
        setIsReconnecting(true)
        restartSubscription()
        // Also refetch history to fill any gaps
        if (roomId) {
            apolloClient.cache.evict({ id: 'ROOT_QUERY', fieldName: 'history' })
            fetchHistory({ variables: { roomId } })
        }
    }

    function onData(observable) {
        // console.log(`data => ${JSON.stringify(observable.data, null, 4)}`)

        // If we were disconnected, refetch history when connection is restored
        const shouldRefetch = shouldRefetchHistoryRef.current

        // Clear disconnected state when we receive data
        setIsDisconnected(false)
        setIsReconnecting(false)

        // Refetch history if we were disconnected to fill any gaps
        if (shouldRefetch && roomId) {
            // console.log('Connection restored after disconnection, refetching history')
            shouldRefetchHistoryRef.current = false
            apolloClient.cache.evict({ id: 'ROOT_QUERY', fieldName: 'history' })
            fetchHistory({ variables: { roomId } })
        }

        const event = observable.data.data.roomEvents
        if (!event) {
            console.warn(`unexpected subscription data: ${JSON.stringify(observable.data, null, 4)}`)
            return
        }

        // Read cache to get current cursor value
        const cachedHistory = apolloClient.cache.readQuery({
            query: History,
            variables: { roomId }
        })

        // If this is a ChatAdded event from the current user, remove the optimistic version
        if (event.__typename === 'ChatAdded' && event.actor.id === user?.id) {
            setOptimisticChats(prev => prev.filter(chat => chat.text !== event.text))
        }

        // If this is a MusicReaction event, trigger animation
        if (event.__typename === 'MusicReaction' && event.track) {
            const animationKey = `${event.track.id}-${event.reaction}`
            setAnimatedReactions(prev => new Set([...prev, animationKey]))
            // Remove animation after it completes
            setTimeout(() => {
                setAnimatedReactions(prev => {
                    const next = new Set(prev)
                    next.delete(animationKey)
                    return next
                })
            }, 800)
        }

        // If this is a ChatReaction event, we don't render it as a visible event
        // but we do add it to cache for aggregation
        if (event.__typename === 'ChatReaction') {
            // Remove the optimistic version if this is from the current user
            if (event.actor.id === user?.id) {
                setOptimisticChatReactions(prev =>
                    prev.filter(r => !(r.chatId === event.chatId && r.emoji === event.emoji))
                )
            }

            // Add to cache but don't add as a visible event
            apolloClient.cache.writeQuery({
                query: History,
                data: {
                    history: {
                        events: [
                            event
                        ],
                        cursor: cachedHistory?.history?.cursor ?? null,
                    }
                },
                variables: {
                    roomId
                }
            })
            return
        }

        // If this is a ChatReactionRemoved event, remove it from cache
        if (event.__typename === 'ChatReactionRemoved') {
            const reactionIdToRemove = event.id
            // console.log(`ChatReactionRemoved event received for reaction ID: ${reactionIdToRemove}`)

            // Remove any optimistic reaction that matches this ID
            setOptimisticChatReactions(prev =>
                prev.filter(r => r.reactionId !== reactionIdToRemove)
            )

            apolloClient.cache.modify({
                id: 'ROOT_QUERY',
                fields: {
                    history(existingHistory, { readField, storeFieldName }) {
                        // Only modify if this is the history for the current room
                        if (!storeFieldName.includes(`"roomId":"${roomId}"`)) {
                            return existingHistory
                        }

                        if (!existingHistory?.events) {
                            return existingHistory
                        }

                        // console.log(`Removing chat reaction ${reactionIdToRemove} from cache for room ${roomId}`)

                        return {
                            ...existingHistory,
                            events: existingHistory.events.filter(eventRef => {
                                const eventId = readField('id', eventRef)
                                return eventId !== reactionIdToRemove
                            })
                        }
                    }
                }
            })

            // Don't add ChatReactionRemoved as a visible event
            return
        }

        // If this is a ChatRemoved event, handle message removal with animation
        if (event.__typename === 'ChatRemoved') {
            const chatIdToRemove = event.id
            // console.log(`ChatRemoved event received for chat ID: ${chatIdToRemove}`)

            // Close menu and deselect if this message was selected
            if (selectedMessageId === chatIdToRemove) {
                setSelectedMessageId(null)
                setMenuPosition(null)
            }

            // Add to removing set to trigger fade-out animation
            setRemovingMessages(prev => new Set([...prev, chatIdToRemove]))

            // After animation completes, remove from cache
            setTimeout(() => {
                // Use cache.modify to directly modify the events array without triggering merge
                // We need to modify the ROOT_QUERY.history field for this specific roomId
                apolloClient.cache.modify({
                    id: 'ROOT_QUERY',
                    fields: {
                        history(existingHistory, { readField, storeFieldName }) {
                            // Only modify if this is the history for the current room
                            if (!storeFieldName.includes(`"roomId":"${roomId}"`)) {
                                return existingHistory
                            }

                            if (!existingHistory?.events) {
                                return existingHistory
                            }

                            // console.log(`Removing chat ${chatIdToRemove} from cache for room ${roomId}`)

                            return {
                                ...existingHistory,
                                events: existingHistory.events.filter(eventRef => {
                                    const eventId = readField('id', eventRef)
                                    return eventId !== chatIdToRemove
                                })
                            }
                        }
                    }
                })

                // Remove from removing set
                setRemovingMessages(prev => {
                    const next = new Set(prev)
                    next.delete(chatIdToRemove)
                    return next
                })
            }, 300) // Match the animation duration

            // Don't add ChatRemoved to the cache as a visible event
            return
        }

        // If this is a ChatUpdated event, update the existing cache entry instead of adding a new one
        if (event.__typename === 'ChatUpdated') {
            const chatIdToUpdate = event.id
            // console.log(`ChatUpdated event received for chat ID: ${chatIdToUpdate}`)

            // Update the cache by modifying the existing event
            apolloClient.cache.modify({
                id: 'ROOT_QUERY',
                fields: {
                    history(existingHistory, { readField, storeFieldName, toReference }) {
                        // Only modify if this is the history for the current room
                        if (!storeFieldName.includes(`"roomId":"${roomId}"`)) {
                            return existingHistory
                        }

                        if (!existingHistory?.events) {
                            return existingHistory
                        }

                        // console.log(`Updating chat ${chatIdToUpdate} in cache for room ${roomId}`)

                        // Check if we need to update the events array
                        let found = false
                        const updatedEvents = existingHistory.events.map(eventRef => {
                            const eventId = readField('id', eventRef)
                            const typename = readField('__typename', eventRef)

                            if (eventId === chatIdToUpdate && (typename === 'ChatAdded' || typename === 'ChatUpdated')) {
                                found = true
                                // Return a reference to the ChatUpdated entity
                                return toReference(`ChatUpdated:${chatIdToUpdate}`)
                            }
                            return eventRef
                        })

                        // If we found and updated the event, return the modified history
                        if (found) {
                            return {
                                ...existingHistory,
                                events: updatedEvents
                            }
                        }

                        // If not found, return unchanged (shouldn't happen, but be safe)
                        return existingHistory
                    }
                }
            })

            // Now update the actual ChatUpdated cache entry with the new data
            apolloClient.cache.modify({
                id: `ChatUpdated:${chatIdToUpdate}`,
                fields: {
                    text: () => event.text,
                    updated: () => event.updated
                }
            })

            // Don't add ChatUpdated as a new event in the history
            return
        }

        // console.log('updating history query cache with new event')
        apolloClient.cache.writeQuery({
            query: History,
            data: {
                history: {
                    events: [
                        event
                    ],
                    cursor: cachedHistory?.history?.cursor ?? null,
                }
            },
            variables: {
                roomId
            }
        })
    }

    function onError(data) {
        // console.log(`SUBSCRIPTION ERROR: ${JSON.stringify(data)}`)
        if (!validTokenExists()) {
            // console.log('Subscription error and no valid auth token exists — logging out')
            logout()
            return
        }
        setIsDisconnected(true)
        shouldRefetchHistoryRef.current = true
        setTimeout(restartSubscription, 5000)
    }


    function renderEvent(event, reactionsByTrackId = {}) {
        switch (event.__typename) {
            case 'AdminAdded': {
                const { actor: { username: actorUsername }, admin: { username: adminUsername } } = event
                return <InfoBubble icon="add_moderator">{actorUsername} made {adminUsername} a room admin.</InfoBubble>
            }
            case 'AdminRemoved': {
                const { actor: { username: actorUsername }, admin: { username: adminUsername } } = event
                return <InfoBubble icon="remove_moderator">{actorUsername} removed {adminUsername}'s room admin privileges. Music they have contributed will no longer be available to the room.</InfoBubble>
            }
            case 'ChatAdded': {
                const { actor: { id: actorId }, actor, text } = event
                const isSelf = user?.id === actorId
                return <ChatBubble actor={actor} text={text} isSelf={isSelf} />
            }
            case 'ChatRemoved': {
                // ChatRemoved events are silent - the message is simply removed from history
                return null
            }
            case 'ChatUpdated': {
                const { actor: { id: actorId }, actor, text } = event
                const isSelf = user?.id === actorId
                return <ChatBubble actor={actor} text={text} isSelf={isSelf} isEdited={true} />
            }
            case 'ChatReaction': {
                // ChatReaction events are silent - they're aggregated and shown on chat messages
                return null
            }
            case 'ChatReactionRemoved': {
                // ChatReactionRemoved events are silent - the reaction is simply removed
                return null
            }
            case 'MemberAdded': {
                const { actor: { username: actorName }, member: { username: memberName } } = event
                return <InfoBubble icon="group_add">{ memberName } joined the room</InfoBubble>
            }
            case 'MemberRemoved': {
                const { actor: { username: actorName }, member: { username: memberName } } = event
                const message = memberName === actorName ? `${ memberName } left the room` : `${ actorName } removed ${ memberName } from the room`
                return <InfoBubble icon="group_remove">{message}</InfoBubble>
            }
            case 'MusicReaction': {
                // This is now handled by RequestStarted events
                return null
            }
            case 'RequestAdded': {
                const { request: { track }, actor: { username } } = event
                if (!track) return <InfoBubble icon="add">{ username } requested a track that no longer exists</InfoBubble>
                return <InfoBubble icon="add">{ username } requested { trackLink(track) } by <b>{ track.artist }</b></InfoBubble>
            }
            case 'RequestRemoved': {
                const { request: { track }, actor: { username } } = event
                if (!track) return <InfoBubble icon="delete">{ username } removed a track that no longer exists from the request queue.</InfoBubble>
                return <InfoBubble icon="delete">{ username } removed { trackLink(track) } from the request queue</InfoBubble>
            }
            case 'RequestSkipped': {
                const { request: { track }, actor: { username } } = event
                if (!track) return <InfoBubble icon="skip_next">{ username } skipped a track that no longer exists</InfoBubble>
                return <InfoBubble icon="skip_next">{ username } skipped { trackLink(track) }</InfoBubble>
            }
            case 'RequestStarted': {
                const { request: { track } } = event
                if (!track) return <InfoBubble icon="music_note">Now playing a track that no longer exists</InfoBubble>
                const reactions = reactionsByTrackId[track.id]
                return <InfoBubble icon="music_note" reactions={reactions} animatedReactions={animatedReactions} trackId={track.id}>Now playing { trackLink(track) } by <b>{ track.artist }</b></InfoBubble>
            }
            case 'RoomUpdated': {
                const { actor: { username }, updates: { spinning, name, topic } } = event
                if (spinning) {
                    return <InfoBubble icon="text_snippet">{username} changed the spinning tag to <b>{spinning}</b></InfoBubble>
                } else if (name) {
                    return <InfoBubble icon="text_snippet">{username} changed the name of the room to <b>{name}</b></InfoBubble>
                } else if (topic) {
                    return <InfoBubble icon="text_snippet">{username} changed the topic to <b>{topic}</b></InfoBubble>
                } else {
                    return null
                }
            }
            case 'Subscribed':
                return // reconnects are silent
            case 'TrackUpdated': {
                const { actor: { username }, track, updates: { tagsAdded, tagsRemoved } } = event
                if (!track) return <InfoBubble icon="local_offer">{username} updated tags on a track that no longer exists</InfoBubble>

                const messages = []
                if (tagsAdded) {
                    const tags = tagsAdded.split(',').map(tag => tag.trim()).filter(Boolean)
                    tags.forEach(tag => {
                        messages.push(<InfoBubble icon="local_offer" key={`add-${tag}`}>{username} added tag <b>{tag}</b> to { trackLink(track) } by <b>{ track.artist }</b></InfoBubble>)
                    })
                }
                if (tagsRemoved) {
                    const tags = tagsRemoved.split(',').map(tag => tag.trim()).filter(Boolean)
                    tags.forEach(tag => {
                        messages.push(<InfoBubble icon="local_offer" key={`remove-${tag}`}>{username} removed tag <b>{tag}</b> from { trackLink(track) } by <b>{ track.artist }</b></InfoBubble>)
                    })
                }

                return messages.length > 0 ? <>{messages}</> : null
            }
            default:
                // console.log(`unhandled room event data: ${JSON.stringify(event)}`)
        }
    }


    async function handleDeleteMessage(chatId) {
        try {
            await removeChatMutation({ variables: { roomId, chatId } })
            setSelectedMessageId(null)
            setMenuPosition(null)
        } catch (error) {
            console.error('failed to delete chat', error)
        }
    }

    function handleEditMessage(chatId, currentText) {
        setEditingMessageId(chatId)
        setEditingText(currentText)
        setOriginalText(currentText)
        setSelectedMessageId(null)
        setMenuPosition(null)
    }

    async function handleSaveEdit(chatId) {
        if (!editingText || editingText.trim().length === 0) {
            return
        }

        // Store the new text for optimistic update
        const newText = editingText

        // Exit edit mode immediately for better UX
        setEditingMessageId(null)
        setEditingText('')
        setOriginalText('')

        // Immediately update the cache to show the edited message
        // Find the cache ID for the chat event - try both ChatAdded and ChatUpdated
        const chatAddedId = `ChatAdded:${chatId}`
        const chatUpdatedId = `ChatUpdated:${chatId}`

        // Try to modify the ChatAdded entry first
        try {
            apolloClient.cache.modify({
                id: chatAddedId,
                fields: {
                    text: () => newText,
                    __typename: () => 'ChatUpdated'
                }
            })
        } catch (e) {
            // If that doesn't exist, try ChatUpdated
            apolloClient.cache.modify({
                id: chatUpdatedId,
                fields: {
                    text: () => newText
                }
            })
        }

        try {
            await editChatMutation({
                variables: {
                    roomId,
                    chatId,
                    text: newText
                }
            })
        } catch (error) {
            console.error('failed to edit chat', error)
            // On error, the subscription will eventually fix the cache
        }
    }

    function handleCancelEdit() {
        setEditingMessageId(null)
        setEditingText('')
    }

    function handleOtherMessageClick(event, messageId) {
        event.stopPropagation()

        // Toggle selection
        if (hoveredOtherMessageId === messageId) {
            setHoveredOtherMessageId(null)
            setOtherMessageMenuPosition(null)
        } else {
            setHoveredOtherMessageId(messageId)

            // Calculate menu position relative to the clicked element
            const rect = event.currentTarget.getBoundingClientRect()
            const containerRect = terminalContainerRef.current?.getBoundingClientRect()

            // Default position: centered above the message
            let top = rect.top + window.scrollY
            let left = rect.left + rect.width / 2 + window.scrollX

            // Estimate menu height (emoji picker button is roughly 40px tall + padding)
            const menuHeight = 56

            // Check if menu would go above the chat container
            if (containerRect) {
                const menuTop = rect.top - menuHeight - 8 // 8px margin
                const containerTop = containerRect.top

                // If menu would appear above container, position it below the message instead
                if (menuTop < containerTop) {
                    top = rect.bottom + window.scrollY
                    // Use a different transform for bottom positioning
                    setOtherMessageMenuPosition({
                        top,
                        left,
                        positionBelow: true
                    })
                    return
                }
            }

            setOtherMessageMenuPosition({
                top,
                left,
                positionBelow: false
            })
        }
    }

    async function handleAddChatReaction(chatId, emoji) {
        // console.log('handleAddChatReaction called', { chatId, emoji, roomId })

        // Check if the user has already reacted with this emoji (including optimistic reactions)
        const hasOptimisticReaction = optimisticChatReactions.some(
            r => r.chatId === chatId && r.emoji === emoji && r.userId === user?.id && !r.removing
        )

        if (hasOptimisticReaction) {
            // console.log('User has already added this reaction optimistically, skipping')
            setHoveredOtherMessageId(null)
            setOtherMessageMenuPosition(null)
            return
        }

        const chatReactions = reactionsByChatId[chatId]
        if (chatReactions) {
            const existingReaction = chatReactions.find(r => r.emoji === emoji)
            if (existingReaction) {
                const userAlreadyReacted = existingReaction.users.some(u => u.userId === user?.id)
                if (userAlreadyReacted) {
                    // console.log('User has already reacted with this emoji, skipping')
                    // Close the menu without adding duplicate reaction
                    setHoveredOtherMessageId(null)
                    setOtherMessageMenuPosition(null)
                    return
                }
            }
        }

        // Add optimistic reaction
        const optimisticReaction = {
            id: `optimistic-${Date.now()}-${Math.random()}`,
            chatId,
            emoji,
            username: user?.username,
            userId: user?.id,
            reactionId: null // Will be set when the real reaction comes back
        }
        setOptimisticChatReactions(prev => [...prev, optimisticReaction])

        // Close both menus immediately for better UX
        setHoveredOtherMessageId(null)
        setOtherMessageMenuPosition(null)
        setSelectedMessageId(null)
        setMenuPosition(null)

        try {
            const result = await addChatReactionMutation({
                variables: {
                    roomId,
                    chatId,
                    emoji
                }
            })
            // console.log('addChatReaction result:', result)
        } catch (error) {
            console.error('failed to add chat reaction', error)
            // Remove the optimistic reaction on error
            setOptimisticChatReactions(prev => prev.filter(r => r.id !== optimisticReaction.id))
        }
    }

    async function handleRemoveChatReaction(reactionId) {
        // Find the reaction in optimistic reactions or in the history
        const optimisticReaction = optimisticChatReactions.find(r => r.reactionId === reactionId)

        // If it's an optimistic reaction that hasn't been confirmed yet, just remove it
        if (optimisticReaction) {
            setOptimisticChatReactions(prev => prev.filter(r => r.reactionId !== reactionId))
        } else {
            // For confirmed reactions, add to optimistic removals
            // We need to find the reaction details from the history to track it
            let reactionToRemove = null
            Object.entries(reactionsByChatId).forEach(([chatId, reactions]) => {
                reactions.forEach(reaction => {
                    const user = reaction.users.find(u => u.reactionId === reactionId)
                    if (user) {
                        reactionToRemove = {
                            id: `removing-${reactionId}`,
                            chatId,
                            emoji: reaction.emoji,
                            reactionId,
                            removing: true
                        }
                    }
                })
            })

            if (reactionToRemove) {
                // Mark this reaction as being removed optimistically
                setOptimisticChatReactions(prev => [...prev, reactionToRemove])
            }
        }

        try {
            await removeChatReactionMutation({
                variables: {
                    roomId,
                    reactionId
                }
            })
        } catch (error) {
            console.error('failed to remove chat reaction', error)
            // On error, restore the optimistic state
            setOptimisticChatReactions(prev =>
                prev.filter(r => r.id !== `removing-${reactionId}` && r.reactionId !== reactionId)
            )
        }
    }

    function handleMessageClick(event, messageId) {
        event.stopPropagation()

        // Toggle selection
        if (selectedMessageId === messageId) {
            setSelectedMessageId(null)
            setMenuPosition(null)
        } else {
            setSelectedMessageId(messageId)

            // Calculate menu position relative to the clicked element
            const rect = event.currentTarget.getBoundingClientRect()
            const containerRect = terminalContainerRef.current?.getBoundingClientRect()

            // Default position: centered above the message
            let top = rect.top + window.scrollY
            let left = rect.left + rect.width / 2 + window.scrollX

            // Estimate menu height (edit + delete + emoji picker + padding)
            const menuHeight = 140

            // Check if menu would go above the chat container
            if (containerRect) {
                const menuTop = rect.top - menuHeight - 8 // 8px margin
                const containerTop = containerRect.top

                // If menu would appear above container, position it below the message instead
                if (menuTop < containerTop) {
                    top = rect.bottom + window.scrollY
                    // Use a different transform for bottom positioning
                    setMenuPosition({
                        top,
                        left,
                        positionBelow: true
                    })
                    return
                }
            }

            setMenuPosition({
                top,
                left,
                positionBelow: false
            })
        }
    }

    function trackLink(track) {
        return <TrackLinkPopover track={track} roomId={roomId} />
    }

    function updateInput(e) {
        const newText = e.target.value.replace('\n', '')
        setLine(newText)
    }

    // Inserts given text at the current caret position in the textarea
    function insertAtCursor(text) {
        const input = inputRef.current
        if (!input) {
            setLine((prev) => (prev ?? '') + text)
            return
        }
        const start = input.selectionStart ?? line.length
        const end = input.selectionEnd ?? line.length
        const next = (line ?? '')
        const newValue = next.slice(0, start) + text + next.slice(end)
        setLine(newValue)
        // Restore focus and move caret to just after inserted emoji
        setTimeout(() => {
            try {
                input.focus()
                const pos = start + text.length
                input.setSelectionRange(pos, pos)
            } catch {}
        }, 0)
    }

    // Build a map of reactions for each track, aggregating per user
    const reactionsByTrackId = {}
    historyData?.history.events.toReversed().forEach(event => {
        if (event.__typename === 'MusicReaction' && event.track) {
            const trackId = event.track.id
            if (!reactionsByTrackId[trackId]) {
                reactionsByTrackId[trackId] = {}
            }
            const userId = event.actor.id
            const username = event.actor.username

            // Initialize user's reaction count if needed
            if (!reactionsByTrackId[trackId][userId]) {
                reactionsByTrackId[trackId][userId] = { username, count: 0 }
            }

            // Add +1 for thumbs up, -1 for thumbs down
            const delta = event.reaction === 'thumbsUp' ? 1 : -1
            const newValue = reactionsByTrackId[trackId][userId].count + delta
            reactionsByTrackId[trackId][userId].count = Math.max(-1, Math.min(newValue, 1))
        }
    })

    // Convert to thumbsUp/thumbsDown arrays based on net reactions
    Object.keys(reactionsByTrackId).forEach(trackId => {
        const userReactions = reactionsByTrackId[trackId]
        const thumbsUp = []
        const thumbsDown = []

        Object.values(userReactions).forEach(({ username, count }) => {
            if (count > 0) {
                thumbsUp.push({ username })
            } else if (count < 0) {
                thumbsDown.push({ username })
            }
            // count === 0 means no net reaction, so we skip it
        })

        reactionsByTrackId[trackId] = { thumbsUp, thumbsDown }
    })

    // Build a map of reactions for each chat message
    const reactionsByChatId = {}
    historyData?.history.events.toReversed().forEach(event => {
        if (event.__typename === 'ChatReaction') {
            const chatId = event.chatId
            if (!reactionsByChatId[chatId]) {
                reactionsByChatId[chatId] = {}
            }
            const userId = event.actor.id
            const username = event.actor.username
            const emoji = event.emoji
            const reactionId = event.id

            // Group by emoji
            if (!reactionsByChatId[chatId][emoji]) {
                reactionsByChatId[chatId][emoji] = []
            }

            // Add user to the reaction list with the reaction ID
            reactionsByChatId[chatId][emoji].push({ username, userId, reactionId })
        }
    })

    // Merge optimistic reactions
    optimisticChatReactions.forEach(optReaction => {
        const { chatId, emoji, username, userId, reactionId, removing } = optReaction

        if (removing) {
            // This is an optimistic removal - filter out the reaction being removed
            if (reactionsByChatId[chatId] && reactionsByChatId[chatId][emoji]) {
                reactionsByChatId[chatId][emoji] = reactionsByChatId[chatId][emoji].filter(
                    user => user.reactionId !== reactionId
                )
                // Clean up empty emoji arrays
                if (reactionsByChatId[chatId][emoji].length === 0) {
                    delete reactionsByChatId[chatId][emoji]
                }
            }
        } else {
            // This is an optimistic addition
            if (!reactionsByChatId[chatId]) {
                reactionsByChatId[chatId] = {}
            }
            if (!reactionsByChatId[chatId][emoji]) {
                reactionsByChatId[chatId][emoji] = []
            }

            // Only add if not already present (avoid duplicates)
            const alreadyExists = reactionsByChatId[chatId][emoji].some(
                user => user.userId === userId
            )
            if (!alreadyExists) {
                reactionsByChatId[chatId][emoji].push({ username, userId, reactionId })
            }
        }
    })

    // Convert to arrays of reactions with usernames
    Object.keys(reactionsByChatId).forEach(chatId => {
        const emojiReactions = reactionsByChatId[chatId]
        const reactions = []

        Object.entries(emojiReactions).forEach(([emoji, users]) => {
            if (users.length > 0) {
                reactions.push({
                    emoji,
                    users,
                    count: users.length
                })
            }
        })

        reactionsByChatId[chatId] = reactions
    })

    const rowTypes = new Set(['ChatAdded','ChatUpdated','ChatRemoved','AdminAdded','AdminRemoved','MemberAdded','MemberRemoved','RequestAdded','RequestRemoved','RequestSkipped','RequestStarted','RoomUpdated','TrackUpdated'])

    // Debug: log event count and first few event IDs
    // console.log(`[render] historyData.events count: ${historyData?.history?.events?.length ?? 0}`)
    // if (historyData?.history?.events?.length > 0) {
    //     const recentEvents = historyData.history.events.slice(0, 3)
    //     recentEvents.forEach((e, i) => {
    //         console.log(`[render] events[${i}]: id=${e?.id}, __typename=${e?.__typename}, actor=${e?.actor?.username}`)
    //     })
    // }

    // Group consecutive chat events from the same user
    const groupedEvents = []
    let currentGroup = null

    historyData?.history.events.toReversed().forEach(event => {
        // Skip MusicReaction, ChatReaction, and ChatReactionRemoved events as they're shown inline
        if (event.__typename === 'MusicReaction' || event.__typename === 'ChatReaction' || event.__typename === 'ChatReactionRemoved') {
            return
        }

        const isChatEvent = event.__typename === 'ChatAdded' || event.__typename === 'ChatUpdated'

        if (isChatEvent && currentGroup && currentGroup.actorId === event.actor.id) {
            // Add to current group
            currentGroup.messages.push({
                id: event.id,
                text: event.text,
                isEdited: event.__typename === 'ChatUpdated'
            })
            currentGroup.lastEventId = event.id
        } else {
            // Save previous group if it exists
            if (currentGroup) {
                groupedEvents.push(currentGroup)
            }

            // Start new group for chat events, or add non-chat event directly
            if (isChatEvent) {
                currentGroup = {
                    type: 'chatGroup',
                    actorId: event.actor.id,
                    actor: event.actor,
                    messages: [{
                        id: event.id,
                        text: event.text,
                        isEdited: event.__typename === 'ChatUpdated'
                    }],
                    firstEventId: event.id,
                    lastEventId: event.id
                }
            } else {
                groupedEvents.push({ type: 'event', event })
                currentGroup = null
            }
        }
    })

    // Don't forget the last group
    if (currentGroup) {
        groupedEvents.push(currentGroup)
    }

    const chatLines = groupedEvents.map(item => {
        if (item.type === 'chatGroup') {
            const isSelf = user?.id === item.actorId
            // Get reactions for all messages in this group
            const messagesWithReactions = item.messages.map(msg => ({
                ...msg,
                reactions: reactionsByChatId[msg.id] || []
            }))

            const content = (
                <ChatBubble
                    actor={item.actor}
                    messages={messagesWithReactions}
                    isSelf={isSelf}
                    selectedMessageId={isSelf ? selectedMessageId : hoveredOtherMessageId}
                    onMessageClick={isSelf ? handleMessageClick : handleOtherMessageClick}
                    removingMessages={removingMessages}
                    editingMessageId={editingMessageId}
                    editingText={editingText}
                    onEditingTextChange={setEditingText}
                    onSaveEdit={handleSaveEdit}
                    onCancelEdit={handleCancelEdit}
                    onRemoveReaction={handleRemoveChatReaction}
                    onAddReaction={handleAddChatReaction}
                    currentUserId={user?.id}
                />
            )
            return <div key={item.firstEventId}>{ content }</div>
        } else {
            const event = item.event
            const content = renderEvent(event, reactionsByTrackId)
            if (rowTypes.has(event.__typename)) {
                return <div key={event.id}>{ content }</div>
            } else {
                return <div className="ChatLine" key={event.id}>{ content }</div>
            }
        }
    }).filter(Boolean)

    // Add optimistic chat messages
    const optimisticChatLines = optimisticChats.map(chat => {
        const content = (
            <div style={{ opacity: 0.6 }}>
                <ChatBubble actor={user} text={chat.text} isSelf={true} />
            </div>
        )
        return <div key={chat.id}>{ content }</div>
    })

    let emptyContent
    if (!user) {
        return <div style={{ width: '100%', textAlign: 'center' }}><Spinner/></div>
    } else if (!user.username) {
        return (
            <div className="ChatPage">
                <div className="ChatEmpty">
                    <p>You haven't set your username.  You need a username to chat.</p>
                    <p>You can set your username on the <span onClick={() => dispatch(Actions.showProfileModal())} className="Link">profile screen</span>.</p>
                </div>
            </div>
        )
    } else if (!room) {
        emptyContent = (
            <div className="ChatEmpty">
                <p>You are not in a room!  Would you like to be?</p>
                <p>Open the <span onClick={() => pushModal('rooms')} className="Link">Rooms</span> list to find a room.</p>
            </div>
        )
    } else if (room.members.length < 2) {
        emptyContent = (
            <div className="ChatEmpty">
                <p>You are the only member of this listening room.</p>
                <p><span onClick={() => dispatch(Actions.showRoomDetailsModal(room.id))} className="Link">Invite your friends</span> to join you and listen together.</p>
            </div>
        )
    }

    return (
        <div className="ChatPage">
            <div className="ChatTopFade" />
            { room ? [
                <div key={2} className="TerminalContainer" ref={terminalContainerRef} onScroll={handleScroll}>
                    <ChatContainer>
                        { loadingHistory ? <div style={{ width: '100%', textAlign: 'center' }}><Spinner/></div> : null }
                        { chatLines }
                        { optimisticChatLines }
                    </ChatContainer>
                    <div className="ChatBottomFade" />
                </div>,
                <div key={3} className="TerminalInputContainer">
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                        <Textarea
                            ref={inputRef}
                            autosize
                            minRows={3}
                            maxRows={3}
                            className="TerminalInput"
                            placeholder="Type a message and press Enter to send it."
                            value={line}
                            onChange={updateInput}
                            onKeyDown={handleKeyPress}
                            variant="unstyled"
                            styles={{ input: { color: '#fff', backgroundColor: 'transparent' } }}
                            style={{ backgroundColor: 'transparent', flex: 1 }}
                            data-tour="chat-input"
                        />
                        <div style={{ display: 'flex', flexDirection: 'row', gap: 6, alignItems: 'flex-start' }}>
                            <EmojiPickerButton onSelect={insertAtCursor} onClose={() => inputRef.current?.focus()} />
                            {speechRecognitionSupported && (
                                <button
                                    type="button"
                                    className="VoiceTrigger"
                                    aria-label={isRecording ? "Stop recording" : "Start voice input"}
                                    title={isRecording ? "Stop recording" : "Start voice input"}
                                    onClick={handleVoiceInput}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: isRecording ? '#ff6b6b' : 'var(--mantine-color-gray-1, #fff)',
                                        opacity: isRecording ? 1 : 0.8,
                                    }}
                                >
                                    <span className="material-symbols-outlined">
                                        {isRecording ? 'stop_circle' : 'mic'}
                                    </span>
                                </button>
                            )}
                            <button
                                type="button"
                                className="SendTrigger"
                                aria-label="Send message"
                                title="Send message"
                                onClick={handleSendClick}
                                disabled={!line || line.length === 0}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: (!line || line.length === 0) ? 'default' : 'pointer',
                                    color: 'var(--mantine-color-gray-1, #fff)',
                                    opacity: (!line || line.length === 0) ? 0.4 : 1,
                                }}
                            >
                                <span className="material-symbols-outlined">send</span>
                            </button>
                        </div>
                    </div>
                </div>
            ] : null }
            { emptyContent }
            {selectedMessageId && menuPosition && (() => {
                // Find the selected message's text from grouped events
                let selectedMessageText = ''
                for (const item of groupedEvents) {
                    if (item.type === 'chatGroup') {
                        const msg = item.messages.find(m => m.id === selectedMessageId)
                        if (msg) {
                            selectedMessageText = msg.text
                            break
                        }
                    }
                }

                return (
                    <div
                        ref={menuRef}
                        style={{
                            position: 'fixed',
                            top: menuPosition.top,
                            left: menuPosition.left,
                            transform: menuPosition.positionBelow
                                ? 'translate(-50%, 0)'
                                : 'translate(-50%, -100%)',
                            backgroundColor: 'rgba(30, 30, 30, 0.95)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '8px',
                            padding: '4px',
                            zIndex: 1000,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                            marginBottom: menuPosition.positionBelow ? 0 : '8px',
                            marginTop: menuPosition.positionBelow ? '8px' : 0
                        }}
                    >
                        <button
                            onClick={() => handleEditMessage(selectedMessageId, selectedMessageText)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 16px',
                                background: 'transparent',
                                border: 'none',
                                color: '#4dabf7',
                                cursor: 'pointer',
                                fontSize: '14px',
                                borderRadius: '4px',
                                transition: 'background-color 0.2s',
                                width: '100%',
                                textAlign: 'left'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(77, 171, 247, 0.1)'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>edit</span>
                            Edit
                        </button>
                        <button
                            onClick={() => handleDeleteMessage(selectedMessageId)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 16px',
                                background: 'transparent',
                                border: 'none',
                                color: '#ff6b6b',
                                cursor: 'pointer',
                                fontSize: '14px',
                                borderRadius: '4px',
                                transition: 'background-color 0.2s',
                                width: '100%',
                                textAlign: 'left'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 107, 107, 0.1)'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>delete</span>
                            Delete
                        </button>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '4px',
                                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                                marginTop: '4px'
                            }}
                        >
                            <EmojiPickerButton
                                onSelect={(emoji) => {
                                    // console.log('Emoji selected for own message:', emoji)
                                    handleAddChatReaction(selectedMessageId, emoji)
                                }}
                                onClose={() => inputRef.current?.focus()}
                                placement={menuPosition.positionBelow ? "bottom" : "top"}
                            />
                        </div>
                    </div>
                )
            })()}
            {hoveredOtherMessageId && otherMessageMenuPosition && (
                <div
                    ref={otherMessageMenuRef}
                    style={{
                        position: 'fixed',
                        top: otherMessageMenuPosition.top,
                        left: otherMessageMenuPosition.left,
                        transform: otherMessageMenuPosition.positionBelow
                            ? 'translate(-50%, 0)'
                            : 'translate(-50%, -100%)',
                        backgroundColor: 'rgba(30, 30, 30, 0.95)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        padding: '8px',
                        zIndex: 1000,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                        marginBottom: otherMessageMenuPosition.positionBelow ? 0 : '8px',
                        marginTop: otherMessageMenuPosition.positionBelow ? '8px' : 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <EmojiPickerButton
                        onSelect={(emoji) => {
                            // console.log('Emoji selected:', emoji)
                            handleAddChatReaction(hoveredOtherMessageId, emoji)
                        }}
                        onClose={() => inputRef.current?.focus()}
                        placement={otherMessageMenuPosition.positionBelow ? "bottom" : "top"}
                    />
                </div>
            )}
            {isDisconnected && roomId && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1001,
                        gap: '16px'
                    }}
                >
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '16px',
                        padding: '32px',
                        backgroundColor: 'rgba(30, 30, 30, 0.95)',
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
                    }}>
                        <span
                            className="material-symbols-outlined"
                            style={{
                                fontSize: '48px',
                                color: '#ff6b6b',
                                animation: 'pulse 2s infinite'
                            }}
                        >
                            cloud_off
                        </span>
                        <div style={{ textAlign: 'center' }}>
                            <h3 style={{ margin: '0 0 8px 0', color: '#fff' }}>Connection Lost</h3>
                            <p style={{ margin: 0, color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
                                {isReconnecting ? 'Reconnecting...' : 'Attempting to reconnect automatically...'}
                            </p>
                        </div>
                        <button
                            onClick={handleManualReconnect}
                            disabled={isReconnecting}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: isReconnecting ? '#666' : '#4dabf7',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: '500',
                                cursor: isReconnecting ? 'not-allowed' : 'pointer',
                                transition: 'background-color 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                opacity: isReconnecting ? 0.6 : 1
                            }}
                            onMouseEnter={(e) => {
                                if (!isReconnecting) e.target.style.backgroundColor = '#339af0'
                            }}
                            onMouseLeave={(e) => {
                                if (!isReconnecting) e.target.style.backgroundColor = '#4dabf7'
                            }}
                        >
                            <span className="material-symbols-outlined" style={{
                                fontSize: '20px',
                                animation: isReconnecting ? 'spin 1s linear infinite' : 'none'
                            }}>
                                {isReconnecting ? 'sync' : 'refresh'}
                            </span>
                            {isReconnecting ? 'Reconnecting...' : 'Reconnect Now'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}
